import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
    signInAnonymously,
    signInWithPopup,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    OAuthProvider,
    type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

/**
 * Plan §20.6 — auth provider.
 *
 * - Anonymous sign-in fires on first load so /v1 public reads work
 *   without an explicit account.
 * - signInWithGoogle / signInWithApple use popup flow (web-native).
 * - signOut returns to anonymous mode so the app is never logged
 *   out without a session.
 */
interface AuthContextValue {
    user: User | null;
    loading: boolean;
    isSignedIn: boolean;
    isAnonymous: boolean;
    signInWithGoogle: () => Promise<void>;
    signInWithApple: () => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
    user: null,
    loading: true,
    isSignedIn: false,
    isAnonymous: false,
    signInWithGoogle: async () => undefined,
    signInWithApple: async () => undefined,
    signOut: async () => undefined,
});

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            if (!u) {
                try {
                    await signInAnonymously(auth);
                } catch (err) {
                    console.warn('[auth] anonymous sign-in failed', err);
                    setUser(null);
                    setLoading(false);
                }
            } else {
                setUser(u);
                setLoading(false);
            }
        });
        return () => unsub();
    }, []);

    const signInWithGoogle = useCallback(async () => {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
    }, []);

    const signInWithApple = useCallback(async () => {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        await signInWithPopup(auth, provider);
    }, []);

    const signOut = useCallback(async () => {
        await firebaseSignOut(auth);
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isSignedIn: !!user && !user.isAnonymous,
                isAnonymous: !!user && user.isAnonymous,
                signInWithGoogle,
                signInWithApple,
                signOut,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
